import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Route as RouteIcon, Sparkles, Clock, Fuel, TrendingDown, AlertTriangle, Gauge, Navigation } from "lucide-react";
import api, { zar } from "@/lib/api";
import { PageHeader, StatCard, Spinner } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const RISK_STYLES = {
  High: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  Medium: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  Low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
};

function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { timeStyle: "short", dateStyle: "short" }) : "—"; }

export default function RouteAI() {
  const [eta, setEta] = useState(null);
  const [loadingEta, setLoadingEta] = useState(true);
  const [cities, setCities] = useState([]);
  const [depot, setDepot] = useState("Johannesburg");
  const [plan, setPlan] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  const loadEta = async () => {
    setLoadingEta(true);
    try { setEta((await api.get("/ops/predictive-eta")).data); }
    catch { toast.error("Failed to load predictions"); }
    setLoadingEta(false);
  };

  useEffect(() => {
    loadEta();
    api.get("/public/cities").then((r) => setCities(r.data.cities)).catch(() => {});
  }, []);

  const optimize = async () => {
    setOptimizing(true);
    try {
      const r = await api.post("/ops/route-optimize", { depot });
      setPlan(r.data);
      toast.success(`Optimised ${r.data.stop_count} stops · saved ${r.data.saved_km} km`);
    } catch { toast.error("Optimisation failed"); }
    setOptimizing(false);
  };

  return (
    <div>
      <PageHeader title="Route AI" subtitle="AI-driven route optimisation & predictive delay ETAs">
        <Button variant="outline" onClick={loadEta} data-testid="routeai-refresh">
          <Gauge size={16} className="mr-2" /> Refresh predictions
        </Button>
      </PageHeader>

      {/* Route optimiser */}
      <div className="rounded-xl border border-border bg-card p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-accent text-primary flex items-center justify-center"><RouteIcon size={18} /></div>
          <div>
            <h2 className="font-heading font-bold">Multi-drop route optimiser</h2>
            <p className="text-xs text-muted-foreground">Nearest-neighbour sequencing across all active deliveries</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="eyebrow text-muted-foreground block mb-1.5">Depot / start</label>
            <select data-testid="routeai-depot" value={depot} onChange={(e) => setDepot(e.target.value)}
              className="h-10 rounded-md border border-border bg-background px-3 text-sm min-w-[180px]">
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Button className="bg-primary hover:bg-red-700 h-10" onClick={optimize} disabled={optimizing} data-testid="routeai-optimize">
            <Sparkles size={16} className="mr-2" /> {optimizing ? "Optimising…" : "Optimise active routes"}
          </Button>
        </div>

        {plan && (
          <div className="mt-5" data-testid="routeai-plan">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard testid="plan-distance" label="Optimised distance" value={`${plan.optimized_km} km`} icon={RouteIcon} />
              <StatCard testid="plan-saved" label="Distance saved" value={`${plan.saved_km} km`} sub={`${plan.saved_pct}% shorter`} icon={TrendingDown} accent="text-emerald-600" />
              <StatCard testid="plan-hours" label="Est. drive time" value={`${plan.est_hours} h`} icon={Clock} accent="text-blue-600" />
              <StatCard testid="plan-cost" label="Cost saved" value={zar(plan.cost_saved_zar)} sub={`${plan.fuel_saved_l} L fuel`} icon={Fuel} accent="text-orange-600" />
            </div>
            {plan.ai_insight && (
              <div className="rounded-xl border border-primary/30 bg-accent p-4 mb-4 text-sm" data-testid="plan-ai">
                <p className="flex items-center gap-2 font-semibold text-primary mb-1"><Sparkles size={15} /> Ebik Roadman</p>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{plan.ai_insight}</p>
              </div>
            )}
            {plan.breach_count > 0 && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300" data-testid="plan-breach-banner">
                <AlertTriangle size={16} /> {plan.breach_count} stop(s) breach the promised delivery window — consider re-sequencing or reassigning.
              </div>
            )}
            <ol className="space-y-2">
              <li className="flex items-center gap-3 text-sm font-semibold"><span className="w-6 h-6 rounded-full bg-[#0B0F17] text-white text-xs flex items-center justify-center">S</span> {plan.depot} <span className="text-muted-foreground font-normal">(depot)</span></li>
              {plan.stops.map((s, i) => (
                <li key={s.city} className="flex items-center gap-3 text-sm" data-testid={`plan-stop-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">{i + 1}</span>
                  <span className="font-semibold">{s.city}</span>
                  <span className="text-muted-foreground">· {s.leg_km} km · {s.tracking_numbers.join(", ")}</span>
                  {s.eta && <span className={`ml-auto text-xs font-mono ${s.breach ? "text-red-600 font-bold" : "text-primary"}`}>ETA {new Date(s.eta).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}</span>}
                  {s.breach && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300" data-testid={`plan-stop-breach-${i}`}>LATE {Math.max(...s.breaches.map((b) => b.late_minutes))}m</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Predictive ETA */}
      {loadingEta ? <Spinner /> : eta && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard testid="eta-total" label="Live shipments" value={eta.summary.total} icon={Navigation} />
            <StatCard testid="eta-high" label="High risk" value={eta.summary.high} icon={AlertTriangle} accent="text-red-600" />
            <StatCard testid="eta-medium" label="Medium risk" value={eta.summary.medium} icon={AlertTriangle} accent="text-orange-600" />
            <StatCard testid="eta-low" label="On plan" value={eta.summary.low} icon={Gauge} accent="text-emerald-600" />
          </div>

          {eta.ai_insight && (
            <div className="rounded-xl border border-primary/30 bg-accent p-4 mb-4 text-sm" data-testid="eta-ai">
              <p className="flex items-center gap-2 font-semibold text-primary mb-1"><Sparkles size={15} /> Dispatcher briefing</p>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{eta.ai_insight}</p>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="p-4 border-b border-border"><h2 className="font-heading font-bold">Predictive ETA & delay risk</h2></div>
            <div className="divide-y divide-border">
              {eta.predictions.length === 0 && <p className="p-6 text-sm text-muted-foreground">No live shipments in transit.</p>}
              {eta.predictions.map((p) => (
                <div key={p.id} className="p-4" data-testid={`eta-row-${p.tracking_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-sm">{p.tracking_number}</span>
                      <StatusBadge status={p.status} />
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${RISK_STYLES[p.risk_level]}`}>{p.risk_level} risk</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">ETA {fmt(p.eta_predicted)}</p>
                      <p className="text-xs text-muted-foreground">{p.confidence_pct}% confidence · {p.delay_risk_minutes > 0 ? `+${p.delay_risk_minutes} min risk` : "on time"}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{p.route} · {p.distance_remaining_km} km remaining · ~{p.hours_remaining} h</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.factors.map((f, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
